/** Tiny data hooks: cached GETs with refresh, and the realtime account room. */
import { applyFrame, type AccountLive, type RoomFrame } from "@dukun/protocol";
import { useCallback, useEffect, useRef, useState } from "react";
import { api, wsUrl } from "./api.ts";

const cache = new Map<string, unknown>();
const listeners = new Map<string, Set<() => void>>();
export function invalidate(prefix: string) {
  for (const k of [...cache.keys()]) if (k.startsWith(prefix)) cache.delete(k);
  for (const [k, ls] of listeners) if (k.startsWith(prefix)) for (const l of ls) l();
}

export function useApi<T>(path: string | null, opts: { refreshMs?: number } = {}) {
  const [data, setData] = useState<T | undefined>(() => (path ? (cache.get(path) as T | undefined) : undefined));
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((x) => x + 1), []);
  useEffect(() => {
    if (!path) return;
    const set = listeners.get(path) ?? new Set();
    set.add(reload); listeners.set(path, set);
    return () => { set.delete(reload); };
  }, [path, reload]);
  useEffect(() => {
    if (!path) return;
    let alive = true;
    if (cache.has(path)) setData(cache.get(path) as T);
    api<T>(path).then((d) => { if (!alive) return; cache.set(path, d); setData(d); setError(null); }).catch((e: Error) => alive && setError(e));
    const iv = opts.refreshMs ? setInterval(reload, opts.refreshMs) : undefined;
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [path, tick, opts.refreshMs, reload]);
  return { data, error, loading: !data && !error, reload };
}

/** Live account view over the AccountRoom socket: snapshot, merge-patch deltas, reconnect with backoff,
 *  `stale` when no frame for 5 s while the socket should be open (docs/06-cloudflare.md §7). */
export function useAccountLive(accountId: string | null) {
  const [live, setLive] = useState<AccountLive | null>(null);
  const [status, setStatus] = useState<"connecting" | "open" | "closed">("connecting");
  const ref = useRef<AccountLive | null>(null);
  useEffect(() => {
    if (!accountId) return;
    let ws: WebSocket | null = null, stop = false, backoff = 1000, ping: ReturnType<typeof setInterval> | undefined;
    const connect = () => {
      setStatus("connecting");
      ws = new WebSocket(wsUrl(`/ws/accounts/${accountId}`));
      ws.onopen = () => { backoff = 1000; setStatus("open"); ping = setInterval(() => ws?.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })), 25_000); };
      ws.onmessage = (e) => {
        const f = JSON.parse(String(e.data)) as RoomFrame;
        if (f.type === "snapshot") ref.current = f.live;
        else if (f.type === "delta" && ref.current) {
          if (f.seq <= ref.current.seq) return; // duplicate
          if (f.seq > ref.current.seq + 1) { ws?.send(JSON.stringify({ type: "resync" })); return; } // gap: ask for a snapshot
          ref.current = { ...ref.current, ...f.patch };
        } else return;
        setLive(ref.current);
      };
      ws.onclose = () => { if (ping) clearInterval(ping); setStatus("closed"); if (!stop) setTimeout(connect, backoff = Math.min(backoff * 2, 30_000)); };
    };
    connect();
    return () => { stop = true; ws?.close(); if (ping) clearInterval(ping); };
  }, [accountId]);
  return { live, status };
}

export { applyFrame };

/** The user's own socket (UserRoom): chat token streams, backtest progress. */
export function useUserEvents(onMessage: (m: Record<string, unknown>) => void) {
  const cb = useRef(onMessage);
  cb.current = onMessage;
  useEffect(() => {
    let ws: WebSocket | null = null, stop = false, backoff = 1000;
    const connect = () => {
      ws = new WebSocket(wsUrl("/ws/me"));
      ws.onopen = () => { backoff = 1000; };
      ws.onmessage = (e) => { try { cb.current(JSON.parse(String(e.data))); } catch { /* ignore */ } };
      ws.onclose = () => { if (!stop) setTimeout(connect, backoff = Math.min(backoff * 2, 30_000)); };
    };
    connect();
    return () => { stop = true; ws?.close(); };
  }, []);
}
